import type { Quizlet } from './quizlet';
import type { SubscriptionTier } from './subscription';

/**
 * Metadata for a quizlet within a deck group (list view - no questions)
 */
export interface DeckGroupQuizletMeta {
  quizlet_id: string;
  name: string;
  question_count: number;
  added_at: string;
}

/**
 * Deck Group for list view (metadata only)
 */
export interface DeckGroup {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  quizlets: DeckGroupQuizletMeta[];
  total_questions: number;
}

/**
 * Deck Group with full quiz data (for starting sessions)
 */
export interface DeckGroupWithQuestions extends Omit<DeckGroup, 'quizlets'> {
  quizlets: Quizlet[];
}

/**
 * Request to create a new deck group
 */
export interface CreateDeckGroupRequest {
  name: string;
  description?: string;
  quizlet_ids: string[];
}

/**
 * Request to update a deck group
 */
export interface UpdateDeckGroupRequest {
  name?: string;
  description?: string;
  quizlet_ids?: string[];
}

/**
 * Filters for listing deck groups
 */
export interface DeckGroupFilters {
  search?: string;
}

/**
 * Tier limits for deck groups
 */
export interface DeckGroupTierLimits {
  maxQuizzesPerGroup: number | null;
}

export const DECK_GROUP_TIER_LIMITS: Record<SubscriptionTier, DeckGroupTierLimits> = {
  foundation: {
    maxQuizzesPerGroup: 5,
  },
  deep_dive: {
    maxQuizzesPerGroup: 10,
  },
  mastermind: {
    maxQuizzesPerGroup: null,
  },
};

/**
 * Check if user can add more quizzes to a group based on tier
 */
export function canAddQuizToGroup(
  currentCount: number,
  tier: SubscriptionTier
): boolean {
  const limits = DECK_GROUP_TIER_LIMITS[tier];
  if (limits.maxQuizzesPerGroup === null) return true;
  return currentCount < limits.maxQuizzesPerGroup;
}

/**
 * Get maximum quizzes allowed per group for a tier
 */
export function getMaxQuizzesPerGroup(tier: SubscriptionTier): number | null {
  return DECK_GROUP_TIER_LIMITS[tier].maxQuizzesPerGroup;
}

/**
 * Check if group exceeds tier limit (for downgrade scenarios)
 */
export function groupExceedsTierLimit(
  quizletCount: number,
  tier: SubscriptionTier
): boolean {
  const limits = DECK_GROUP_TIER_LIMITS[tier];
  if (limits.maxQuizzesPerGroup === null) return false;
  return quizletCount > limits.maxQuizzesPerGroup;
}
